"use client"

import { minimizeAnalyticsProperties, minimizeFlagEvaluationProperties } from "./privacy"
import { isPostHogTelemetryEnabled } from "../monitoring/telemetry-config"

/**
 * Client-side PostHog event tracking.
 *
 * Works with the PostHog JS SDK initialized in instrumentation-client.ts.
 * The SDK exposes `window.posthog` and handles distinct_id, session,
 * batching, and API key automatically.
 *
 * Usage:
 *   trackEvent("signup_modal_opened")
 *   trackEvent("character_created", { style: "anime", category: "girlfriend" })
 *   identifyUser(userId, { email, subscription })
 *   resetUser()  // on logout
 */

type EventProperties = Record<string, unknown>

const isDev = process.env.NODE_ENV === "development"

interface PostHogInstance {
  get_property?: (key: string) => unknown
  resetPersonPropertiesForFlags?: (reload?: boolean) => void
  setPersonPropertiesForFlags?: (props: EventProperties, reload?: boolean) => void
  capture: (event: string, properties?: EventProperties) => void
  identify: (
    distinctId: string,
    properties?: Record<string, unknown>
  ) => void
  reset: (resetDeviceId?: boolean) => void
}

function getPostHog(): PostHogInstance | null {
  if (!isPostHogTelemetryEnabled()) return null
  if (typeof window === "undefined") return null
  const ph = (window as unknown as { posthog?: PostHogInstance }).posthog
  if (!ph || typeof ph.capture !== "function") {
    if (isDev) {
      console.warn("[analytics] PostHog SDK not available on window.posthog")
    }
    return null
  }
  return ph
}

function redactTraitsForLog(
  traits: Record<string, unknown> | undefined
): Record<string, unknown> | undefined {
  if (!traits) return undefined
  return {
    ...traits,
    ...(typeof traits.email === "string" && { email: "[redacted]" }),
  }
}

/**
 * Track a custom event via the PostHog JS SDK.
 *
 * @param event - Event name (e.g. "signup_modal_opened", "character_created")
 * @param properties - Optional properties attached to the event
 *
 * @example
 * trackEvent("signup_modal_opened", { source: "hero_cta" })
 * trackEvent("image_generated", { style: "anime", credits_used: 10 })
 */
export function trackEvent(
  event: string,
  properties?: EventProperties
): void {
  const ph = getPostHog()
  if (!ph) return

  if (isDev) {
    console.log(
      `%c[analytics] capture: "${event}"`,
      "color: #87CEFA;",
      properties ?? ""
    )
  }

  ph.capture(event, properties)
}

/**
 * Identify an authenticated user. Links the anonymous PostHog session
 * to the real user ID so all past and future events are attributed.
 *
 * Call once when the user's profile is loaded after authentication.
 *
 * @param userId - Supabase user UUID
 * @param traits - Optional user properties to set (email, subscription, etc.)
 */
export function identifyUser(
  userId: string,
  traits?: Record<string, unknown>
): void {
  const ph = getPostHog()
  if (!ph) return

  if (isDev) {
    console.log(
      `%c[analytics] identify: "${userId}"`,
      "color: #90EE90;",
      redactTraitsForLog(traits) ?? ""
    )
  }

  // Flag evaluation can send identify traits before before_send runs.
  minimizeFlagEvaluationProperties(ph)
  ph.identify(userId, traits ? minimizeAnalyticsProperties(traits) : undefined)
}

/**
 * Read the Trackdesk click ID from the trakdesk_cid cookie (set by Trackdesk SDK).
 * Cookie value is JSON: {"tenantId":"pleasurai","cid":"..."}
 * Returns the cid string, or null if not present.
 */
export function getTrackdeskClickId(): string | null {
  if (typeof document === "undefined") return null
  const match = document.cookie.match(/(?:^|;\s*)trakdesk_cid=([^;]*)/)
  if (!match) return null
  try {
    const parsed = JSON.parse(decodeURIComponent(match[1]))
    return parsed?.cid ?? null
  } catch {
    return null
  }
}

/**
 * Reset PostHog identity on logout.
 * Creates a new anonymous distinct_id for the next session.
 */
export function resetUser(): void {
  const ph = getPostHog()
  if (!ph) return

  if (isDev) {
    console.log("%c[analytics] reset: identity cleared", "color: #FFD700;")
  }

  ph.reset()
  minimizeFlagEvaluationProperties(ph)
}
